// Mobile gesture-handlers — bag som passas till WorkflowCanvas via shell:erna.
// Long-press → öppna MobileContextMenuOverlay (controlled, mountad i
// IndexBody). Tidigare försökte vi syntetisera 'contextmenu' MouseEvent
// men Radix lyssnar inte på dispatchEvent.
// Sidebar/topbar/sheet-list-toggling går via CustomEvents så sub-layouts
// kan lyssna utan att vi måste tråda extra state genom shell-kedjan.

import { useMemo } from 'react';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useMobileContextMenu } from '@/contexts/MobileContextMenuContext';

interface UseMobileGesturesArgs {
  canUndo: boolean;
  onUndo: () => void;
}

export function useMobileGestures({ canUndo, onUndo }: UseMobileGesturesArgs) {
  const { sheets, activeSheetIndex, setActiveSheetIndex } = useWorkflow();
  const { open: mobileCtxMenuOpen } = useMobileContextMenu();

  return useMemo(() => ({
    onSheetSwitch: (direction: 'fwd' | 'back') => {
      const next = direction === 'fwd'
        ? Math.min(sheets.length - 1, activeSheetIndex + 1)
        : Math.max(0, activeSheetIndex - 1);
      if (next !== activeSheetIndex) setActiveSheetIndex(next);
    },
    onOpenSheetList: () => {
      window.dispatchEvent(new CustomEvent('engicoro:open-sheet-list'));
    },
    onUndo: () => { if (canUndo) onUndo(); },
    // Long-press → öppna controlled MobileContextMenuOverlay vid touch-
    // punkten. Tidigare synthetic MouseEvent('contextmenu')-pathen
    // fungerade inte på Radix (pointer-event-tracking ignorerar
    // dispatchEvent). Overlay-routern äger UI:n istället.
    onBlockLongPress: (blockId: string, x: number, y: number) => {
      mobileCtxMenuOpen({ kind: 'block', id: blockId, x, y });
    },
    onGroupCardLongPress: (groupId: string, x: number, y: number) => {
      mobileCtxMenuOpen({ kind: 'group-card', id: groupId, x, y });
    },
    onCanvasLongPress: (x: number, y: number) => {
      mobileCtxMenuOpen({ kind: 'canvas', x, y });
    },
    onOpenLeftSidebar: () => {
      window.dispatchEvent(new CustomEvent('engicoro:open-left-sidebar'));
    },
    onOpenRightSidebar: () => {
      window.dispatchEvent(new CustomEvent('engicoro:open-right-sidebar'));
    },
    onOpenTopBar: () => {
      window.dispatchEvent(new CustomEvent('engicoro:open-top-bar'));
    },
  }), [activeSheetIndex, sheets.length, setActiveSheetIndex, canUndo, onUndo, mobileCtxMenuOpen]);
}
